import { t } from "i18next";
import React, { useEffect, useState } from "react";
import styles from "styles.module.css";
import CustomButton, { TextButton } from "ui/Button";
import { IUserLog, UserProfile } from "interface/user";
import { getGetInternationalCountryCodeApi } from "api/common";
import { InternationalCountryCode } from "interface/common";
import { IUserProfileVessel } from "interface/vessel";

import { Loading } from "ui/Loading";
import { useNavigate, useParams } from "react-router";
import { UserVesselsTab } from "./utils/UserVessel";
import {
	adminUserProfileBuyersApi,
	getUserVesselsApi,
	getUserByIdApi,
} from "api/administration";
import { UserTerminalTab } from "./utils/UserTerminal";
import { IBuyer } from "interface/buyer";
import { ProfileBuyer } from "./utils/ProfileBuyers";
import { AdminChangePassword } from "./utils/ChangePassword";
import CustomStepper from "ui/CustomStepper";
import { AdminUserInformation } from "ui/userprofile/AdminUserInformation";
import { COLOR, ICON_NAMES } from "utils/constant";
import { PageTypePath } from "utils/pages";
import "./userInformation.css";
import { getUserLogsApi } from "api/administration";
import CustomModal from "ui/modal/Modal";
import UserLog from "ui/userprofile/UserLog";
import UserLogMobile from "ui/userprofile/UserLogMobile";
import { useInfoStore } from "store/error";
import { CustomError, InfoAction } from "interface/index";
import { usePageAccess } from "hooks/usePageAccess";

enum UserProfileSettingTabs {
	Information = "UserInformation",
	Vessel = "Vessel",
	Terminal = "Terminal",
	Buyer = "Buyer",
	Password = "Password",
}

const settingsTabs = Object.values(UserProfileSettingTabs);

export const UserInformationPage = () => {
	const [user, setUser] = React.useState<UserProfile | null>(null);
	const [countryCodes, setCountryCodes] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [selectedTab, setSelectedTab] = React.useState<UserProfileSettingTabs>(
		UserProfileSettingTabs.Information,
	);

	const [Vessels, setVessels] = React.useState<IUserProfileVessel[]>([]);
	const [profileBuyers, setProfileBuyers] = React.useState<IBuyer[]>([]);
	const [userUpdated, setUserUpdated] = React.useState(false);
	const [allItemsSaved, setAllItemsSaved] = useState(true);
	const [userLogs, setUserLogs] = useState<IUserLog[]>([]);

	const [loadingUser, setLoadingUser] = React.useState(true);
	const [tabs, setTabs] = React.useState(settingsTabs);
	const [step, setStep] = useState(0);
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	const [showUserLogs, setShowUserLogs] = useState(false);
	const { setInfo } = useInfoStore();
	const { pageAccess } = usePageAccess();

	let { id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		if (!id) return;
		getGetInternationalCountryCodeApi()
			.then((response) => {
				setCountryCodes(response);
			})
			.catch((error) => {
				console.log(error);
			});
		getUserVesselsApi(id as string)
			.then((response) => {
				setVessels(response.data);
			})
			.catch((error) => {
				console.log(error);
			});

		adminUserProfileBuyersApi({ userId: id as string })
			.then((response) => {
				setProfileBuyers(response.data);
			})
			.catch((error) => {
				console.log(error);
			});
	}, [id]);

	useEffect(() => {
		if (!user) {
			setLoadingUser(true);
		}
		if (!id) return;
		getUserByIdApi(id as string)
			.then((response) => {
				setUser(response.data as UserProfile);
				setLoadingUser(false);
			})
			.catch((error) => {
				console.log(error);
			})
			.finally(() => {
				setLoadingUser(false);
			});
	}, [userUpdated, id]);

	// useEffect(() => {
	// 	if (!id) return;
	// 	getUserVesselsApi(id as string)
	// 		.then((response) => {
	// 			setVessels(response.data);
	// 		})
	// 		.catch((error) => {
	// 			console.log(error);
	// 		});
	// }, [id]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!user) return;
		const _tabs = [
			UserProfileSettingTabs.Information,
			UserProfileSettingTabs.Password,
		];
		if (user.userGroup.some((role) => role.vessel)) {
			_tabs.push(UserProfileSettingTabs.Vessel);
		}
		if (user.userGroup.some((role) => role.terminal)) {
			_tabs.push(UserProfileSettingTabs.Terminal);
		}
		if (user.userGroup.some((role) => role.buyer)) {
			_tabs.push(UserProfileSettingTabs.Buyer);
		}

		setTabs(_tabs);
	}, [user]);

	const getUserLogs = () => {
		getUserLogsApi(id as string)
			.then((response) => {
				setUserLogs(response.data);
				setShowUserLogs(true);
			})
			.catch((error) => {
				handleError("Error", "Failed to fetch user logs");
				console.log(error);
			});
	};

	const handleError = (
		title: string,
		message: string,
		actions: InfoAction[] = [],
	) => {
		// setError(error);
		setInfo({
			description: message,
			title,
			infoType: "Error",
			show: true,
			actions: actions,
		});
	};

	const handleStepTabs = (step: number) => {
		const tab = tabs[step];
		setSelectedTab(tab);
		setStep(step);
	};

	const handleNextStep = () => {
		if (step < tabs.length - 1) {
			handleStepTabs(step + 1);
			return;
		}

		navigate(`/${PageTypePath.Users}`);
	};

	const handlePreviousStep = () => {
		if (step > 0) {
			const prevStep = step - 1;
			handleStepTabs(prevStep);
		}
	};

	const isFirstStep = step === 0;
	const isLastStep = step === tabs.length - 1;

	if (loadingUser) return <Loading />;
	return (
		<div
			className={`${styles.PageContainer} user-information-page`}
			style={{
				maxHeight: "90%",
				height: isMobileView ? "auto" : "calc(100dvh - 205px)",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
			}}
		>
			<div
				style={{
					maxHeight: "90%",
					// height: isMobileView ? "auto" : "calc(100dvh - 205px)",
				}}
			>
				<div className="page-header" style={{}}>
					<legend className={styles.BoxHeadingLarge}>{t("UpdateUser")}</legend>
				</div>
				<div className="user-information-content">
					<CustomStepper
						className="user-information-stepper"
						steps={tabs.map((tab) => ({
							label: t(tab),
							optional: false,
						}))}
						activeStep={step}
						setActiveStep={handleStepTabs}
					/>

					<div className="user-information-panel">
						{selectedTab === UserProfileSettingTabs.Information && user && (
							<AdminUserInformation
								user={user as any}
								setUser={setUser}
								countryCodes={countryCodes.map(
									(code) => code.internationalDialing,
								)}
								setCountryCode={() => {}}
								adminPage={true}
								setAllItemsSaved={setAllItemsSaved}
								companyAdmin={false}
							/>
						)}

						{selectedTab === UserProfileSettingTabs.Password && (
							<div>
								<AdminChangePassword />
							</div>
						)}

						{selectedTab === UserProfileSettingTabs.Vessel && (
							<div>
								<div style={{ marginBottom: "10px", textAlign: "center" }}>
									<UserVesselsTab vessels={Vessels} setVessels={setVessels} />
								</div>
							</div>
						)}
						{selectedTab === UserProfileSettingTabs.Terminal && (
							<div>
								<div style={{ marginBottom: "10px", textAlign: "center" }}>
									<UserTerminalTab
										user={user as UserProfile}
										setUser={setUser}
										setUserUpdated={setUserUpdated}
									/>
								</div>
							</div>
						)}
						{selectedTab === UserProfileSettingTabs.Buyer && (
							<div>
								<div style={{ marginBottom: "10px", textAlign: "center" }}>
									<ProfileBuyer
										buyers={profileBuyers}
										setBuyers={setProfileBuyers}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			<div
				className="user-information-actions-row"
				style={{
					display: "flex",
					justifyContent: "space-between",
					padding: "20px",
					marginBottom: 0,
					backgroundColor: "#fff",
				}}
			>
				<div className="user-information-actions-left">
					{isFirstStep && (
						<div
							style={{
								display: "flex",
								gap: "20px",
								flexDirection: isMobileView ? "column" : "row",
								width: "100%",
								marginBottom: isMobileView ? "25px" : "",
							}}
						>
							<div
								className="user-information-saved-indicator"
								style={{ marginBottom: "3px" }}
							>
								<span
									style={{ paddingTop: "7px" }}
									className={styles.TableText}
								>
									{t("AllChangesSaved")}
								</span>
								<span style={{ paddingTop: "7px" }}>
									<img
										// src={`/icon/checkmark_green.svg`}
										src={`/icon/${
											allItemsSaved ? "checkmark_green" : ICON_NAMES.Checkbox
										}.svg`}
										alt={"complete icon"}
									/>
								</span>
							</div>
							{pageAccess?.special1 && (
								<CustomButton
									label={t("See") + " " + t("Log")}
									bgColor={COLOR.Green}
									labelStyle={{ color: "white" }}
									onClick={() => {
										getUserLogs();
									}}
								/>
							)}

							{showUserLogs && (
								<CustomModal
									title={t("UserLogs")}
									onClose={() => setShowUserLogs(false)}
									style={{
										width: isMobileView ? "100%" : "69%",
										marginRight: isMobileView ? "5px" : "0px",
										marginLeft: isMobileView ? "5px" : "0px",
									}}
									actions={
										[
											// {
											// 	label: t("Cancel"),
											// 	onClick: () => setShowDeleteConfirm(false),
											// 	bgColor: COLOR.Green,
											// 	color: "white",
											// },
											// {
											// 	label: "Ok",
											// 	onClick: () => {
											// 		setShowDeleteConfirm(false);
											// 		handleDelete();
											// 	},
											// },
										]
									}
								>
									<div style={{ width: "100%" }}>
										{isMobileView ? (
											<UserLogMobile items={userLogs} loading={false} />
										) : (
											<UserLog data={userLogs} />
										)}
									</div>
								</CustomModal>
							)}
						</div>
					)}
				</div>
				<div className="user-information-actions-right">
					<CustomButton
						label={t("Previous")}
						onClick={handlePreviousStep}
						disabled={isFirstStep}
						iconName={ICON_NAMES.Prevous}
						iconRight={false}
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
					<CustomButton
						label={isLastStep ? t("Complete") : t("Next")}
						onClick={handleNextStep}
						labelStyle={{
							color: "white",
						}}
						bgColor={COLOR.Green}
						iconName={
							isLastStep ? ICON_NAMES.FinishTransparent : ICON_NAMES.NextWhite
						}
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				</div>
			</div>
		</div>
	);
};

export default UserInformationPage;
